import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';

import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  MAX_FILES_ALLOWED,
  MAX_FILE_SIZE_MB,
  MAX_IMAGE_SIZE_MB,
  MAX_VIDEOS_ALLOWED,
  MAX_VIDEO_SIZE_GB,
} from './constants';

export const getFileType = (mimeType: string) => {
  const parts = mimeType.split('/');
  const fileType = parts[0];
  if (ACCEPTED_VIDEO_TYPES.includes(fileType)) {
    return PostAttachmentType.VideoUpload;
  }
  if (ACCEPTED_IMAGE_TYPES.includes(fileType)) {
    return PostAttachmentType.Image;
  }
  return PostAttachmentType.File;
};

export const getFileExtension = (fileName?: string) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

export const validateFiles = (
  files: File[],
  existingAttachments: Array<{ attachmentType: PostAttachmentType }> = [],
  t: (key: string, params?: Record<string, string | number>) => string
): string | null => {
  const fileTypeRegex = ACCEPTED_FILE_TYPES.replace(/,/g, '|').replace('*', '.*');

  // Check total file count limit
  if (files.length + existingAttachments.length > MAX_FILES_ALLOWED) {
    return t('file-validation-too-many-files', {
      maxFiles: MAX_FILES_ALLOWED,
      defaultValue: `Only ${MAX_FILES_ALLOWED} video can be added to each post.`,
    });
  }

  // Separate files by type for specific validation
  const videoFiles: File[] = [];
  const imageFiles: File[] = [];
  const otherFiles: File[] = [];

  for (const file of files) {
    const fileType = getFileType(file.type);
    switch (fileType) {
      case PostAttachmentType.VideoUpload:
        videoFiles.push(file);
        break;
      case PostAttachmentType.Image:
        imageFiles.push(file);
        break;
      default:
        otherFiles.push(file);
        break;
    }
  }

  // Validate video files
  const videoValidationError = validateVideoFiles(videoFiles, existingAttachments, t);
  if (videoValidationError) {
    return videoValidationError;
  }

  // Validate image files
  const imageValidationError = validateImageFiles(imageFiles, t);
  if (imageValidationError) {
    return imageValidationError;
  }

  // Validate other files
  const otherFilesValidationError = validateOtherFiles(otherFiles, fileTypeRegex, t);
  if (otherFilesValidationError) {
    return otherFilesValidationError;
  }

  return null;
};

const validateVideoFiles = (
  videoFiles: File[],
  existingAttachments: Array<{ attachmentType: PostAttachmentType }>,
  t: (key: string, params?: Record<string, string | number>) => string
): string | null => {
  const maxVideoSizeInBytes = MAX_VIDEO_SIZE_GB * 1024 * 1024 * 1024;

  // Check video count limit (considering existing videos)
  const existingVideoCount =
    existingAttachments?.filter((att) => att.attachmentType === PostAttachmentType.VideoUpload)
      .length || 0;
  const totalVideoCount = videoFiles.length + existingVideoCount;
  if (totalVideoCount > MAX_VIDEOS_ALLOWED) {
    return t('file-validation-too-many-videos', {
      maxVideos: MAX_VIDEOS_ALLOWED,
      defaultValue: `Only ${MAX_VIDEOS_ALLOWED} video can be added to each post.`,
    });
  }

  // Check video file sizes
  for (const file of videoFiles) {
    if (file.size > maxVideoSizeInBytes) {
      return t('file-validation-video-too-large', {
        maxSize: MAX_VIDEO_SIZE_GB,
        defaultValue: `Videos must be less than ${MAX_VIDEO_SIZE_GB} GB.`,
      });
    }
  }

  return null;
};

const validateImageFiles = (
  imageFiles: File[],
  t: (key: string, params?: Record<string, string | number>) => string
): string | null => {
  const maxImageSizeInBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  // Check image file sizes
  for (const file of imageFiles) {
    if (file.size > maxImageSizeInBytes) {
      return t('file-validation-image-too-large', {
        fileName: file.name,
        maxSize: MAX_IMAGE_SIZE_MB,
        defaultValue: `Image file ${file.name} is too large. Maximum image size is ${MAX_IMAGE_SIZE_MB}MB.`,
      });
    }
  }

  return null;
};

const validateOtherFiles = (
  otherFiles: File[],
  fileTypeRegex: string,
  t: (key: string, params?: Record<string, string | number>) => string
): string | null => {
  const maxFileSizeInBytes = MAX_FILE_SIZE_MB * 1024 * 1024;

  // Check other file sizes and types
  for (const file of otherFiles) {
    if (file.size > maxFileSizeInBytes) {
      return t('file-validation-file-too-large', {
        fileName: file.name,
        maxSize: MAX_FILE_SIZE_MB,
        defaultValue: `File ${file.name} is too large. Maximum file size is ${MAX_FILE_SIZE_MB}MB.`,
      });
    }

    if (!file.type.match(fileTypeRegex)) {
      return t('file-validation-invalid-type', {
        fileName: file.name,
        acceptedTypes: ACCEPTED_FILE_TYPES.replace(/,/g, ', '),
        defaultValue: `File ${file.name} is not accepted. Must be one of the following: ${ACCEPTED_FILE_TYPES.replace(/,/g, ', ')}`,
      });
    }
  }

  return null;
};
